import React, { useState } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, IconButton, Tooltip, Button, Chip, Collapse, Fade,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import CustomSnackbar from "../../../components/CustomSnackbar";
import useBaskets from "../hooks/useBaskets";

// ─── Basket Dialog (Create / Edit) ───────────────────────────
const BasketDialog = ({ open, basket, onClose, onSave }) => {
  const isEdit = !!basket;
  const [description, setDescription] = useState(basket?.description || "");
  const [items, setItems] = useState(
    basket?.items?.length ? basket.items.map((i) => ({ ...i })) : [{ partName: "", description: "" }]
  );
  const [saving, setSaving] = useState(false);

  const handleItemChange = (idx, field, value) => {
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleAddItem = () => setItems((prev) => [...prev, { partName: "", description: "" }]);

  const handleRemoveItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!description.trim()) return;
    const validItems = items.filter((i) => i.partName.trim());
    setSaving(true);
    try {
      await onSave({ description: description.trim(), items: validItems });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl"
      PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" } }}
    >
      <Box sx={{ bgcolor: "#ebf7ff", px: 3, pt: 3, pb: 2 }}>
        <DialogTitle sx={{ p: 0 }}>
          <Typography variant="h6" fontWeight="bold" color="#1f2937">
            {isEdit ? "עריכת סל" : "יצירת סל חדש"}
          </Typography>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <TextField
          label="תיאור הסל"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 3, bgcolor: "#ffffff", borderRadius: 2 }}
          required
        />

        <Typography variant="subtitle2" fontWeight={700} color="#1f2937" mb={1}>
          מק"טים בסל
        </Typography>

        <Box bgcolor="#fafafa" borderRadius={2} border="1px solid rgba(0,0,0,0.08)" mb={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#ebf7ff" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#1f2937" }}>מק"ט</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1f2937" }}>תיאור</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={idx} sx={{ "& > *": { borderBottom: "1px solid rgba(0,0,0,0.05)" } }}>
                    <TableCell sx={{ width: 180 }}>
                      <TextField
                        value={item.partName}
                        onChange={(e) => handleItemChange(idx, "partName", e.target.value)}
                        size="small"
                        placeholder='מק"ט'
                        variant="standard"
                        sx={{ width: "100%" }}
                        inputProps={{ style: { fontSize: "0.85rem" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.description}
                        onChange={(e) => handleItemChange(idx, "description", e.target.value)}
                        size="small"
                        placeholder="תיאור מוצר"
                        variant="standard"
                        sx={{ width: "100%" }}
                        inputProps={{ style: { fontSize: "0.85rem" } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleRemoveItem(idx)}
                        sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          size="small"
          sx={{ color: "#0054A5", textTransform: "none", fontWeight: 600 }}
        >
          הוסף מק"ט
        </Button>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" } }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!description.trim() || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: "#0054A5", borderRadius: 2, px: 3, "&:hover": { bgcolor: "#003d7a" } }}
        >
          {saving ? "שומר..." : "שמור"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Basket Row ───────────────────────────────────────────────
const BasketRow = ({ basket, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`למחוק את הסל "${basket.description}"?`)) return;
    setDeleting(true);
    try {
      await onDelete(basket._id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          bgcolor: open ? "#f0f7ff" : "transparent",
          "& > *": { borderBottom: open ? "none" : "1px solid rgba(0,0,0,0.06)" },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 48, pr: 0 }}>
          <IconButton size="small" sx={{ color: "#0054A5" }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: "#1f2937" }}>{basket.description}</TableCell>
        <TableCell align="center">
          <Chip
            label={`${basket.items?.length || 0} מק"טים`}
            size="small"
            sx={{ bgcolor: "#ebf7ff", color: "#0054A5", fontWeight: 600, fontSize: "0.72rem" }}
          />
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="עריכה">
            <IconButton size="small" onClick={() => onEdit(basket)}
              sx={{ color: "#0054A5", "&:hover": { bgcolor: "#ebf7ff" } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="מחיקה">
            <IconButton size="small" onClick={handleDelete} disabled={deleting}
              sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}
            >
              {deleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: "#f8fafc", px: 4, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {basket.items?.length === 0 ? (
                <Typography variant="body2" color="#9ca3af" py={1}>אין מק"טים בסל</Typography>
              ) : (
                <Table size="small" sx={{ width: "auto", "& td, & th": { px: 2, py: 0.6, fontSize: "0.78rem" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#6b7280", border: "none" }}>מק"ט</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "#6b7280", border: "none" }}>תיאור</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {basket.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 600, color: "#0054A5", border: "none" }}>{item.partName}</TableCell>
                        <TableCell sx={{ color: "#1f2937", border: "none" }}>{item.description || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── BasketsTab ───────────────────────────────────────────────
const BasketsTab = () => {
  const { baskets, loading, snackbar, setSnackbar, createBasket, updateBasket, deleteBasket } = useBaskets();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBasket, setEditingBasket] = useState(null);

  const handleOpenCreate = () => { setEditingBasket(null); setDialogOpen(true); };
  const handleOpenEdit = (basket) => { setEditingBasket(basket); setDialogOpen(true); };
  const handleClose = () => setDialogOpen(false);

  const handleSave = async (data) => {
    if (editingBasket) {
      await updateBasket(editingBasket._id, data);
    } else {
      await createBasket(data);
    }
  };

  return (
    <Box>
      {/* Sub-header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ShoppingBasketIcon sx={{ color: "#0054A5", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} color="#1f2937">
            סלים מוגדרים
          </Typography>
          {!loading && (
            <Chip label={`${baskets.length} סלים`} size="small"
              sx={{ bgcolor: "#ebf7ff", color: "#0054A5", fontWeight: 600, fontSize: "0.72rem" }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ bgcolor: "#0054A5", borderRadius: 2, px: 2.5, "&:hover": { bgcolor: "#003d7a" }, textTransform: "none", fontWeight: 600 }}
        >
          סל חדש
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: "#0054A5" }} />
        </Box>
      )}

      {!loading && baskets.length === 0 && (
        <Fade in timeout={600}>
          <Paper elevation={0} sx={{ borderRadius: 3, p: 6, textAlign: "center", border: "1px solid rgba(0,0,0,0.08)" }}>
            <ShoppingBasketIcon sx={{ fontSize: 56, color: "#d1d5db", mb: 2 }} />
            <Typography variant="h6" color="#6b7280">לא הוגדרו סלים עדיין</Typography>
            <Typography variant="body2" color="#9ca3af" mt={1}>לחץ על "סל חדש" כדי להתחיל</Typography>
          </Paper>
        </Fade>
      )}

      {!loading && baskets.length > 0 && (
        <Fade in timeout={600}>
          <Box bgcolor="#fafafa" borderRadius={2} p={2}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#ebf7ff" }}>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: "bold", color: "#1f2937", py: 1.5 }}>תיאור הסל</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#1f2937" }}>מק"טים</TableCell>
                    <TableCell sx={{ width: 100 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {baskets.map((basket) => (
                    <BasketRow
                      key={basket._id}
                      basket={basket}
                      onEdit={handleOpenEdit}
                      onDelete={deleteBasket}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Fade>
      )}

      {dialogOpen && (
        <BasketDialog
          open={dialogOpen}
          basket={editingBasket}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default BasketsTab;
